import { Flex, Space, Typography } from 'antd';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';

export interface GeoPoint {
  key?: string;
  lat: number;
  lng: number;
}

export interface GoogleReferenceMarker {
  color: string;
  point: GeoPoint;
  scale: number;
  shape?: 'circle' | 'pin';
  title: string;
  zIndex?: number;
}

export interface GoogleSelectedMarker {
  color: string;
  label: string;
  point: GeoPoint;
  title: string;
}

export interface GoogleFloorplanOverlay {
  bounds: () => google.maps.LatLngBounds;
  imageUrl: string;
}

type GoogleMarkerMapLoadState = 'idle' | 'loading' | 'ready' | 'failed';

export function GoogleMarkerMapPicker({
  apiKey,
  failedDescription = translateInventoryText(
    'app.inventory.position.map.googleFailed',
    'Google Maps could not be loaded.',
  ),
  failedFallback,
  focusPoint,
  height,
  loadingDescription,
  manualDescription,
  mapOptions,
  onPick,
  overlay,
  referenceMarkers,
  selectedMarker,
}: {
  apiKey: string;
  failedDescription?: string;
  failedFallback?: ReactElement;
  focusPoint?: GeoPoint | null;
  height: number;
  loadingDescription: string;
  manualDescription?: string;
  mapOptions: google.maps.MapOptions;
  onPick: (point: GeoPoint) => void;
  overlay?: GoogleFloorplanOverlay;
  referenceMarkers: GoogleReferenceMarker[];
  selectedMarker: GoogleSelectedMarker | null;
}) {
  const { loadState, mapElementRef } = useGoogleMarkerMapController({
    apiKey,
    focusPoint,
    mapOptions,
    onPick,
    overlay,
    referenceMarkers,
    selectedMarker,
  });

  if (loadState === 'failed' && failedFallback) {
    return failedFallback;
  }

  return (
    <div
      style={{
        border: '1px solid #d9d9d9',
        borderRadius: 8,
        font: 'inherit',
        height,
        overflow: 'hidden',
        position: 'relative',
        width: '100%',
      }}
    >
      <div ref={mapElementRef} style={{ height: '100%', width: '100%' }} />
      <GoogleMapLoadingOverlay
        apiKey={apiKey}
        failedDescription={failedDescription}
        loadingDescription={loadingDescription}
        manualDescription={manualDescription}
        state={loadState}
      />
    </div>
  );
}

function useGoogleMarkerMapController({
  apiKey,
  focusPoint,
  mapOptions,
  onPick,
  overlay,
  referenceMarkers,
  selectedMarker,
}: {
  apiKey: string;
  focusPoint?: GeoPoint | null;
  mapOptions: google.maps.MapOptions;
  onPick: (point: GeoPoint) => void;
  overlay?: GoogleFloorplanOverlay;
  referenceMarkers: GoogleReferenceMarker[];
  selectedMarker: GoogleSelectedMarker | null;
}) {
  const mapElementRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const overlayRef = useRef<google.maps.GroundOverlay | null>(null);
  const overlayClickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const selectedMarkerRef = useRef<google.maps.Marker | null>(null);
  const referenceMarkerRefs = useRef<google.maps.Marker[]>([]);
  const clickListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const dragListenerRef = useRef<google.maps.MapsEventListener | null>(null);
  const latestMapOptionsRef = useRef(mapOptions);
  const latestOverlayConfigRef = useRef(overlay);
  const latestOnPickRef = useRef(onPick);
  const [loadState, setLoadState] = useState<GoogleMarkerMapLoadState>('idle');

  useEffect(() => {
    latestMapOptionsRef.current = mapOptions;
    latestOverlayConfigRef.current = overlay;
    latestOnPickRef.current = onPick;
  }, [mapOptions, onPick, overlay]);

  useEffect(() => {
    let isMounted = true;

    if (!apiKey.trim()) {
      setLoadState('failed');

      return undefined;
    }

    setLoadState('loading');
    loadGoogleMapsApi(apiKey)
      .then(() => {
        if (!(isMounted && mapElementRef.current)) {
          return;
        }

        const latestMapOptions = latestMapOptionsRef.current;
        const latestOverlayConfig = latestOverlayConfigRef.current;
        const map = mapRef.current ?? new google.maps.Map(mapElementRef.current, latestMapOptions);
        mapRef.current = map;
        map.setOptions(latestMapOptions);
        if (latestOverlayConfig) {
          map.fitBounds(latestOverlayConfig.bounds(), 24);
        }
        setLoadState('ready');
      })
      .catch(() => {
        if (isMounted) {
          setLoadState('failed');
        }
      });

    return () => {
      isMounted = false;
    };
  }, [apiKey]);

  useEffect(() => {
    const map = mapRef.current;

    if (!(loadState === 'ready' && map)) {
      return undefined;
    }

    map.setOptions(mapOptions);
    if (overlay) {
      map.fitBounds(overlay.bounds(), 24);
    }

    return undefined;
  }, [loadState, mapOptions, overlay]);

  useEffect(() => {
    const map = mapRef.current;

    if (!(loadState === 'ready' && map && focusPoint)) {
      return undefined;
    }

    map.setCenter(focusPoint);

    return undefined;
  }, [focusPoint, loadState]);

  useEffect(() => {
    const map = mapRef.current;

    if (!(loadState === 'ready' && map && overlay)) {
      return undefined;
    }

    overlayRef.current?.setMap(null);
    overlayClickListenerRef.current?.remove();
    overlayRef.current = new google.maps.GroundOverlay(overlay.imageUrl, overlay.bounds());
    overlayRef.current.setMap(map);
    overlayClickListenerRef.current = overlayRef.current.addListener('click', (event: google.maps.MapMouseEvent) => {
      const latLng = event.latLng;

      if (latLng) {
        latestOnPickRef.current({ lat: latLng.lat(), lng: latLng.lng() });
      }
    });
    map.fitBounds(overlay.bounds(), 24);

    return () => {
      overlayClickListenerRef.current?.remove();
      overlayClickListenerRef.current = null;
      overlayRef.current?.setMap(null);
      overlayRef.current = null;
    };
  }, [loadState, overlay]);

  useEffect(() => {
    const map = mapRef.current;

    if (!(loadState === 'ready' && map)) {
      return undefined;
    }

    clickListenerRef.current?.remove();
    clickListenerRef.current = map.addListener('click', (event: google.maps.MapMouseEvent) => {
      const latLng = event.latLng;

      if (!latLng) {
        return;
      }

      latestOnPickRef.current({ lat: latLng.lat(), lng: latLng.lng() });
    });

    return () => {
      clickListenerRef.current?.remove();
      clickListenerRef.current = null;
    };
  }, [loadState]);

  useEffect(() => {
    const map = mapRef.current;

    if (!(loadState === 'ready' && map)) {
      return;
    }

    clearGoogleMarkers(referenceMarkerRefs.current);
    referenceMarkerRefs.current = createGoogleReferenceMarkers(map, referenceMarkers);

    selectedMarkerRef.current?.setMap(null);
    dragListenerRef.current?.remove();
    selectedMarkerRef.current = createGoogleSelectedMarker(map, selectedMarker);

    if (selectedMarkerRef.current) {
      dragListenerRef.current = selectedMarkerRef.current.addListener('dragend', () => {
        const position = selectedMarkerRef.current?.getPosition();

        if (position) {
          onPick({ lat: position.lat(), lng: position.lng() });
        }
      });
    }

    return () => {
      clearGoogleMarkers(referenceMarkerRefs.current);
      referenceMarkerRefs.current = [];
      selectedMarkerRef.current?.setMap(null);
      selectedMarkerRef.current = null;
      dragListenerRef.current?.remove();
      dragListenerRef.current = null;
    };
  }, [loadState, onPick, referenceMarkers, selectedMarker]);

  return {
    loadState,
    mapElementRef,
  };
}

function GoogleMapLoadingOverlay({
  apiKey,
  failedDescription,
  loadingDescription,
  manualDescription,
  state,
}: {
  apiKey: string;
  failedDescription: string;
  loadingDescription: string;
  manualDescription?: string;
  state: GoogleMarkerMapLoadState;
}) {
  if (state === 'ready') {
    return null;
  }

  return (
    <Flex
      align="center"
      justify="center"
      style={{ background: '#fafafa', inset: 0, padding: 16, pointerEvents: 'none', position: 'absolute' }}
    >
      <Space direction="vertical" size="small" style={{ padding: 16, textAlign: 'center' }}>
        <Typography.Text type="secondary">
          {!apiKey.trim()
            ? translateInventoryText(
                'app.inventory.position.map.missingApiKey',
                'Set VITE_GOOGLE_MAPS_API_KEY to enable the map.',
              )
            : state === 'failed'
              ? failedDescription
              : loadingDescription}
        </Typography.Text>
        {manualDescription ? <Typography.Text type="secondary">{manualDescription}</Typography.Text> : null}
      </Space>
    </Flex>
  );
}

function createGoogleReferenceMarkers(map: google.maps.Map, referenceMarkers: GoogleReferenceMarker[]) {
  return referenceMarkers.map(
    marker =>
      new google.maps.Marker({
        icon:
          marker.shape === 'pin'
            ? createGoogleMapReferencePinIcon(marker.color)
            : createGoogleMapCircleIcon(marker.color, marker.scale),
        map,
        position: marker.point,
        title: marker.title,
        zIndex: marker.zIndex,
      }),
  );
}

function createGoogleSelectedMarker(map: google.maps.Map, selectedMarker: GoogleSelectedMarker | null) {
  return selectedMarker
    ? new google.maps.Marker({
        draggable: true,
        icon: createGoogleMapPinIcon(selectedMarker.color),
        label: {
          color: '#ffffff',
          fontSize: '12px',
          fontWeight: '700',
          text: selectedMarker.label,
        },
        map,
        position: selectedMarker.point,
        title: selectedMarker.title,
      })
    : null;
}

function clearGoogleMarkers(markers: google.maps.Marker[]) {
  for (const marker of markers) {
    marker.setMap(null);
  }
}

function createGoogleMapCircleIcon(color: string, scale: number): google.maps.Symbol {
  return {
    fillColor: color,
    fillOpacity: 1,
    path: google.maps.SymbolPath.CIRCLE,
    scale,
    strokeColor: '#ffffff',
    strokeWeight: 2,
  };
}

function createGoogleMapPinIcon(color: string): google.maps.Symbol {
  return {
    anchor: new google.maps.Point(12, 22),
    fillColor: color,
    fillOpacity: 1,
    labelOrigin: new google.maps.Point(12, 9),
    path: 'M12 2C8.1 2 5 5.1 5 9c0 5.3 7 13 7 13s7-7.7 7-13c0-3.9-3.1-7-7-7z',
    scale: 1.4,
    strokeColor: '#ffffff',
    strokeWeight: 2,
  };
}

function createGoogleMapReferencePinIcon(color: string): google.maps.Symbol {
  return {
    anchor: new google.maps.Point(10, 18),
    fillColor: color,
    fillOpacity: 1,
    path: 'M10 1.5C6.7 1.5 4 4.2 4 7.5c0 4.5 6 11 6 11s6-6.5 6-11c0-3.3-2.7-6-6-6z',
    scale: 1.25,
    strokeColor: '#ffffff',
    strokeWeight: 2.5,
  };
}

type GoogleMapsWindow = Window &
  typeof globalThis & {
    google?: typeof google;
    __enmonInventoryGoogleMapsReady?: () => void;
  };

let googleMapsApiPromise: Promise<typeof google.maps> | null = null;

function loadGoogleMapsApi(apiKey: string) {
  const googleWindow = window as GoogleMapsWindow;

  if (googleWindow.google?.maps) {
    return Promise.resolve(googleWindow.google.maps);
  }

  if (googleMapsApiPromise) {
    return googleMapsApiPromise;
  }

  googleMapsApiPromise = new Promise((resolve, reject) => {
    googleWindow.__enmonInventoryGoogleMapsReady = () => {
      if (googleWindow.google?.maps) {
        resolve(googleWindow.google.maps);
      } else {
        googleMapsApiPromise = null;
        reject(new Error('Google Maps loaded without the maps API.'));
      }

      googleWindow.__enmonInventoryGoogleMapsReady = undefined;
    };

    const script = document.createElement('script');
    script.async = true;
    script.defer = true;
    script.onerror = () => {
      googleMapsApiPromise = null;
      googleWindow.__enmonInventoryGoogleMapsReady = undefined;
      reject(new Error('Unable to load Google Maps.'));
    };
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
      apiKey,
    )}&callback=__enmonInventoryGoogleMapsReady&v=weekly`;
    document.head.appendChild(script);
  });

  return googleMapsApiPromise;
}
